import { Helmet } from "react-helmet-async";

const SITE_URL = "https://www.hostprotect.io";
const OG_IMAGE = `${SITE_URL}/og-hostprotect.jpg`;

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "HostProtect",
  legalName: "PM Hospitality",
  url: SITE_URL,
  logo: `${SITE_URL}/logo-hostprotect.png`,
  image: OG_IMAGE,
  email: "contact@hostprotect.io",
  telephone: "+33756868432",
  address: {
    "@type": "PostalAddress",
    streetAddress: "27 rue de la gare",
    postalCode: "88380",
    addressLocality: "Arches",
    addressCountry: "FR",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+33756868432",
    contactType: "customer service",
    availableLanguage: ["French", "Spanish", "English"],
  },
};

const productSchemaFR = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "HostProtect — Assurance location courte durée",
  description:
    "Protection complète pour les professionnels de la location courte durée. Couverture vandalisme jusqu'à 50 000€, punaises de lit jusqu'à 10 000€, remboursement sous 48h.",
  brand: { "@type": "Brand", name: "HostProtect" },
  offers: {
    "@type": "Offer",
    price: "149.00",
    priceCurrency: "EUR",
    priceValidUntil: "2026-12-31",
    availability: "https://schema.org/InStock",
    url: `${SITE_URL}/fr/professionnels`,
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "127",
    bestRating: "5",
  },
};

const productSchemaES = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "HostProtect — Seguro alquiler vacacional",
  description:
    "Protección completa para profesionales del alquiler vacacional. Cobertura vandalismo hasta 50.000€, chinches hasta 10.000€, reembolso en 48h.",
  brand: { "@type": "Brand", name: "HostProtect" },
  offers: {
    "@type": "Offer",
    price: "149.00",
    priceCurrency: "EUR",
    priceValidUntil: "2026-12-31",
    availability: "https://schema.org/InStock",
    url: `${SITE_URL}/es/profesionales`,
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "127",
    bestRating: "5",
  },
};

const productSchemaEN = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "HostProtect — Short-term rental insurance",
  description:
    "Complete protection for short-term rental professionals. Vandalism coverage up to €50,000, bed bugs up to €10,000, reimbursement within 48h.",
  brand: { "@type": "Brand", name: "HostProtect" },
  offers: {
    "@type": "Offer",
    price: "149.00",
    priceCurrency: "EUR",
    priceValidUntil: "2026-12-31",
    availability: "https://schema.org/InStock",
    url: `${SITE_URL}/en/professionals`,
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "127",
    bestRating: "5",
  },
};

const faqSchemas = {
  fr: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Quel est le montant de la franchise ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "10% du montant du sinistre, avec un minimum de 150€, quel que soit le type de dommage.",
        },
      },
      {
        "@type": "Question",
        name: "Quels objets ou cas sont exclus ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Vol ou disparition d'objets, même avec effraction. Usure normale, défaut d'entretien, panne ou dysfonctionnement. Dommages causés intentionnellement ou par un tiers non autorisé. Sous-location, usage professionnel ou activité interdite.",
        },
      },
      {
        "@type": "Question",
        name: "Les objets de loisirs sont-ils couverts ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Oui, jusqu'à 1 000€ par an, à condition qu'ils soient mis à disposition des voyageurs et explicitement mentionnés dans l'annonce du logement.",
        },
      },
      {
        "@type": "Question",
        name: "Comment déclarer un sinistre ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Connectez-vous à votre espace client. Déclarez le sinistre dans les 15 jours suivant l'incident. Joignez les documents : constat détaillé, photos du sinistre, facture ou preuve d'achat. L'indemnisation est généralement traitée sous 24 à 48h après validation du dossier.",
        },
      },
    ],
  },
  es: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "¿Cuál es el importe de la franquicia?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "10% del importe del siniestro, con un mínimo de 150€, independientemente del tipo de daño.",
        },
      },
      {
        "@type": "Question",
        name: "¿Qué objetos o casos están excluidos?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Robo o desaparición de objetos, incluso con allanamiento. Desgaste normal, falta de mantenimiento, avería o mal funcionamiento. Daños causados intencionalmente o por un tercero no autorizado.",
        },
      },
      {
        "@type": "Question",
        name: "¿Los objetos de ocio están cubiertos?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Sí, hasta 1.000€ al año, siempre que estén a disposición de los huéspedes y mencionados explícitamente en el anuncio.",
        },
      },
      {
        "@type": "Question",
        name: "¿Cómo declarar un siniestro?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Inicia sesión en tu área de cliente. Declara el siniestro en los 15 días siguientes al incidente. Adjunta: informe detallado, fotos, factura o prueba de compra. La indemnización se procesa generalmente en 24 a 48h tras la validación.",
        },
      },
    ],
  },
  en: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is the deductible amount?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "10% of the claim amount, with a minimum of €150, regardless of the type of damage.",
        },
      },
      {
        "@type": "Question",
        name: "What objects or cases are excluded?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Theft or disappearance of objects, even with break-in. Normal wear and tear, lack of maintenance, breakdown. Intentional damage or damage by unauthorized third party.",
        },
      },
      {
        "@type": "Question",
        name: "Are leisure items covered?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, up to €1,000 per year, provided they are made available to guests and listed in the property ad.",
        },
      },
      {
        "@type": "Question",
        name: "How to report a claim?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Log in to your client area. Report the claim within 15 days of the incident. Attach: detailed report, photos, invoice or proof of purchase. Compensation is usually processed within 24 to 48h after file validation.",
        },
      },
    ],
  },
};

const seoData = {
  home: {
    title: "HostProtect | Assurance location courte durée — Caution Airbnb & Booking",
    description:
      "HostProtect protège vos logements en location courte durée. Assurance vandalisme, punaises de lit, caution Booking & Airbnb. Remboursement sous 48h. Dès 0,40€/jour.",
    keywords:
      "caution booking, assurance location courte durée, garantie location courte durée, assurance airbnb, caution location courte durée, protection logement airbnb",
    canonical: SITE_URL,
    lang: "fr",
  },
  fr: {
    title: "Assurance location courte durée professionnels | HostProtect — Caution Booking & Airbnb",
    description:
      "HostProtect : assurance location courte durée pour gestionnaires, conciergeries et agences. Couverture vandalisme 50 000€, punaises de lit 10 000€, caution Booking & Airbnb. Remboursement 48h. 149€/an/logement.",
    keywords:
      "caution booking, assurance location courte durée, garantie location courte durée, assurance airbnb, caution location courte durée, assurance conciergerie, protection gestionnaire",
    canonical: `${SITE_URL}/fr/professionnels`,
    lang: "fr",
    hreflang: [
      { lang: "fr", url: `${SITE_URL}/fr/professionnels` },
      { lang: "es", url: `${SITE_URL}/es/profesionales` },
      { lang: "en", url: `${SITE_URL}/en/professionals` },
      { lang: "x-default", url: `${SITE_URL}/fr/professionnels` },
    ],
    schema: [organizationSchema, productSchemaFR, faqSchemas.fr],
  },
  es: {
    title: "Seguro alquiler vacacional profesionales | HostProtect — Caución Booking & Airbnb",
    description:
      "HostProtect: seguro alquiler vacacional para gestores y agencias. Cobertura vandalismo 50.000€, chinches 10.000€, caución Booking & Airbnb. Reembolso 48h. 149€/año/propiedad.",
    keywords:
      "seguro alquiler vacacional, caución booking, seguro airbnb, protección alquiler corta duración, garantía alquiler vacacional",
    canonical: `${SITE_URL}/es/profesionales`,
    lang: "es",
    hreflang: [
      { lang: "fr", url: `${SITE_URL}/fr/professionnels` },
      { lang: "es", url: `${SITE_URL}/es/profesionales` },
      { lang: "en", url: `${SITE_URL}/en/professionals` },
      { lang: "x-default", url: `${SITE_URL}/fr/professionnels` },
    ],
    schema: [organizationSchema, productSchemaES, faqSchemas.es],
  },
  en: {
    title: "Short-term rental insurance professionals | HostProtect — Booking & Airbnb coverage",
    description:
      "HostProtect: short-term rental insurance for property managers and agencies. Vandalism coverage €50,000, bed bugs €10,000, Booking & Airbnb deposit protection. 48h reimbursement. €149/year/property.",
    keywords:
      "short-term rental insurance, booking caution, airbnb insurance, vacation rental protection, property damage coverage, rental guarantee",
    canonical: `${SITE_URL}/en/professionals`,
    lang: "en",
    hreflang: [
      { lang: "fr", url: `${SITE_URL}/fr/professionnels` },
      { lang: "es", url: `${SITE_URL}/es/profesionales` },
      { lang: "en", url: `${SITE_URL}/en/professionals` },
      { lang: "x-default", url: `${SITE_URL}/fr/professionnels` },
    ],
    schema: [organizationSchema, productSchemaEN, faqSchemas.en],
  },
  particulares: {
    title: "Seguro alquiler vacacional particulares | HostProtect — Protección Airbnb",
    description:
      "HostProtect para particulares: protege tu propiedad en Airbnb y Booking. Cobertura vandalismo, chinches, reembolso 48h. Desde 0,40€/día.",
    keywords:
      "seguro alquiler vacacional particular, protección airbnb particular, caución booking particular, seguro propiedad vacacional",
    canonical: `${SITE_URL}/es/particulares`,
    lang: "es",
    schema: [organizationSchema],
  },
  notEligible: {
    title: "Liste d'attente | HostProtect",
    description: "Inscrivez-vous à la liste d'attente HostProtect pour être informé du lancement dans votre région.",
    canonical: `${SITE_URL}/not-eligible`,
    lang: "fr",
  },
};

const SEO = ({ page, customTitle, customDescription }) => {
  const data = seoData[page] || seoData.home;
  const title = customTitle || data.title;
  const description = customDescription || data.description;

  return (
    <Helmet>
      <html lang={data.lang || "fr"} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {data.keywords && <meta name="keywords" content={data.keywords} />}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="HostProtect — PM Hospitality" />

      {/* Canonical */}
      {data.canonical && <link rel="canonical" href={data.canonical} />}

      {/* Hreflang */}
      {data.hreflang?.map((h) => (
        <link key={h.lang} rel="alternate" hrefLang={h.lang} href={h.url} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="HostProtect" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={data.canonical || SITE_URL} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1536" />
      <meta property="og:image:height" content="1024" />
      <meta property="og:locale" content={data.lang === "fr" ? "fr_FR" : data.lang === "es" ? "es_ES" : "en_GB"} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {/* JSON-LD Schema */}
      {data.schema?.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
